// Remediation Tier 2 #11 — admin list for CollectionTemplateBundle. Gated
// by Collection.ManageTemplates (same as the template catalog). Routes:
//   /admin/collection-bundles          → list (this page)
//   /admin/collection-bundles/new      → create
//   /admin/collection-bundles/:id      → edit

import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, Plus } from "lucide-react";
import { Badge, Button } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { useToast } from "@/components/ui/Toast";
import {
  collectionBundlesApi,
  type CollectionTemplateBundleDto,
} from "@/api/collectionBundles";
import {
  COLLECTION_AUDIENCES,
  collectionAudienceLabel,
  type MaterialAudience,
} from "@/api/collectionTemplates";
import { invalidateCollectionBundleReads } from "./CollectionTemplateBundleEditPage";

export function CollectionTemplateBundlesPage() {
  // Round-3 #7 — External Collection › Bundles.
  useBreadcrumbs([{ label: "External Collection" }, { label: "Bundles" }]);
  const qc = useQueryClient();
  const { toast } = useToast();
  const [showInactive, setShowInactive] = useState(false);
  const [audienceFilter, setAudienceFilter] = useState<MaterialAudience | "">("");

  const list = useQuery({
    queryKey: ["collection-bundles-admin", showInactive, audienceFilter || null],
    queryFn: () =>
      collectionBundlesApi.list(showInactive, audienceFilter || null),
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => collectionBundlesApi.deactivate(id),
    onSuccess: () => {
      toast({ tone: "success", message: "Bundle deactivated." });
      invalidateCollectionBundleReads(qc);
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  const rows = list.data ?? [];

  const columns: Column<CollectionTemplateBundleDto>[] = [
    {
      key: "name",
      header: "Bundle",
      render: (b) => (
        <Link
          to={`/admin/collection-bundles/${b.id}`}
          className="font-semibold text-onyx hover:text-gold-shadow"
        >
          <div className="flex items-center gap-space-2">
            <span>{b.name}</span>
            {!b.active && <Badge status="archived">Inactive</Badge>}
          </div>
          {b.description && (
            <div className="text-caption text-gray-mid font-normal max-w-md truncate">
              {b.description}
            </div>
          )}
        </Link>
      ),
    },
    {
      key: "audience",
      header: "Audience",
      render: (b) => (
        <span className="text-small text-onyx">
          {b.audience ? collectionAudienceLabel(b.audience) : (b.audienceTag ?? "—")}
        </span>
      ),
    },
    {
      key: "slots",
      header: "Items",
      render: (b) => (
        <span className="text-small text-onyx">{b.slots.length}</span>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (b) =>
        b.active ? (
          <Button
            variant="secondary"
            size="sm"
            type="button"
            onClick={() => {
              if (
                confirm(
                  `Deactivate '${b.name}'? The composer will stop showing it under "Add from group".`,
                )
              ) {
                deactivate.mutate(b.id);
              }
            }}
          >
            <Archive size={14} className="mr-space-1" /> Deactivate
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="flex flex-col gap-space-4">
      <PageHeader
        eyebrow="Admin · Collection"
        title="Collection bundles"
        description="Reusable groupings of templates."
        actions={
          <Link to="/admin/collection-bundles/new">
            <Button type="button">
              <Plus size={16} className="mr-space-1" /> New bundle
            </Button>
          </Link>
        }
      />

      <div className="flex items-end gap-space-3">
        <div>
          <label
            htmlFor="cb-audience"
            className="font-display text-label font-semibold uppercase tracking-label text-gray-mid block mb-space-1"
          >
            Audience
          </label>
          <Select
            id="cb-audience"
            value={audienceFilter}
            onChange={(e) =>
              setAudienceFilter(e.target.value as MaterialAudience | "")
            }
            className="w-48"
          >
            <option value="">All audiences</option>
            {COLLECTION_AUDIENCES.map((a) => (
              <option key={a} value={a}>
                {collectionAudienceLabel(a)}
              </option>
            ))}
          </Select>
        </div>
        <label className="flex items-center gap-space-2 text-small text-onyx pb-space-2">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show inactive
        </label>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(b) => b.id}
        loading={list.isLoading}
        error={list.error instanceof Error ? list.error.message : null}
        emptyMessage="No bundles yet. Click 'New bundle' to author one."
      />
    </div>
  );
}
